import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Description as DocumentIcon,
  PictureAsPdf as PDFIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  GetApp as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Share as ShareIcon
} from '@mui/icons-material';

interface Document {
  id: string;
  name: string;
  type: 'pdf' | 'image' | 'doc' | 'spreadsheet' | 'other';
  category: string;
  dateAdded: string;
  size: string;
}

const DocumentsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  // Mock document data
  const documents: Document[] = [
    { id: 'DOC001', name: 'Business License 2023.pdf', type: 'pdf', category: 'Legal', dateAdded: '2023-01-15', size: '1.2 MB' },
    { id: 'DOC002', name: 'Employee Handbook.pdf', type: 'pdf', category: 'HR', dateAdded: '2023-02-10', size: '3.5 MB' },
    { id: 'DOC003', name: 'Marina Layout.jpg', type: 'image', category: 'Operations', dateAdded: '2023-03-22', size: '5.1 MB' },
    { id: 'DOC004', name: 'Supplier Contracts.doc', type: 'doc', category: 'Legal', dateAdded: '2023-04-05', size: '950 KB' },
    { id: 'DOC005', name: 'Financial Report Q2.pdf', type: 'pdf', category: 'Finance', dateAdded: '2023-07-10', size: '2.3 MB' },
    { id: 'DOC006', name: 'Inventory List.xlsx', type: 'spreadsheet', category: 'Operations', dateAdded: '2023-06-18', size: '1.8 MB' },
    { id: 'DOC007', name: 'Store Policies.pdf', type: 'pdf', category: 'Operations', dateAdded: '2023-05-30', size: '1.5 MB' },
    { id: 'DOC008', name: 'Marketing Plan 2023.pdf', type: 'pdf', category: 'Marketing', dateAdded: '2023-01-25', size: '4.2 MB' },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, docId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocId(docId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocId(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const getDocumentIcon = (type: Document['type']) => {
    switch (type) {
      case 'pdf':
        return <PDFIcon sx={{ color: '#e53935' }} />;
      case 'image':
        return <ImageIcon sx={{ color: '#43a047' }} />;
      case 'doc':
        return <DocumentIcon sx={{ color: '#1e88e5' }} />;
      case 'spreadsheet':
        return <FileIcon sx={{ color: '#00897b' }} />;
      default:
        return <FileIcon sx={{ color: '#757575' }} />;
    }
  };

  const filteredDocuments = documents.filter(doc => 
    doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = Array.from(new Set(documents.map(doc => doc.category)));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Business Documents
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          Upload Document
        </Button>
      </Box>
      <Grid container spacing={3}>
        {/* Search and Filter */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search documents by name or category"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<FilterIcon />}
                    sx={{ flexGrow: 1 }}
                  >
                    Filter
                  </Button>
                  <Button 
                    variant="outlined"
                    sx={{ flexGrow: 1 }}
                  >
                    Sort
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Categories */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Categories
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem button selected>
                <ListItemText primary="All Documents" secondary={`${documents.length} items`} />
              </ListItem>
              {categories.map(category => (
                <ListItem button key={category}>
                  <ListItemText 
                    primary={category} 
                    secondary={`${documents.filter(doc => doc.category === category).length} items`} 
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Documents List */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Documents
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <List>
              {filteredDocuments.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No documents found matching your search." />
                </ListItem>
              ) : (
                filteredDocuments.map((doc) => (
                  <ListItem
                    key={doc.id}
                    divider
                    secondaryAction={
                      <>
                        <IconButton edge="end" onClick={(e) => handleMenuOpen(e, doc.id)}>
                          <MoreIcon />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar>
                        {getDocumentIcon(doc.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={doc.name}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                          <Chip label={doc.category} size="small" />
                          <Typography variant="body2" color="text.secondary">
                            Added: {doc.dateAdded}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Size: {doc.size}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
      {/* Document Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ShareIcon fontSize="small" sx={{ mr: 1 }} />
          Share
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DocumentsPage; 